import { evaluateKeyframes } from '../core/keyframes';
import type { Composition, Layer, Project } from '../core/schema';
import { activeLayersAt, sourceTimeAt } from '../core/timeline';
import { effectToCanvasFilter } from '../core/effects';
import { mediaRegistry } from './mediaRegistry';

const nestedCanvases = new Map<string, HTMLCanvasElement>();

const blendModes: Record<Layer['blendMode'], GlobalCompositeOperation> = {
  normal: 'source-over', multiply: 'multiply', screen: 'screen', overlay: 'overlay', 'soft-light': 'soft-light', darken: 'darken', lighten: 'lighten', difference: 'difference'
};

function resolvedTransform(layer: Layer, time: number) {
  const localTime = time - layer.start;
  return {
    position: evaluateKeyframes(layer.keyframes['transform.position'], localTime, layer.transform.position) as [number, number, number],
    scale: evaluateKeyframes(layer.keyframes['transform.scale'], localTime, layer.transform.scale) as [number, number, number],
    rotation: evaluateKeyframes(layer.keyframes['transform.rotation'], localTime, layer.transform.rotation) as [number, number, number],
    opacity: evaluateKeyframes(layer.keyframes['transform.opacity'], localTime, layer.transform.opacity) as number
  };
}

function applyMask(context: CanvasRenderingContext2D, layer: Layer, composition: Composition) {
  const mask = layer.masks.find((item) => item.mode === 'add');
  if (!mask) return;
  context.beginPath();
  if (mask.type === 'ellipse') {
    const [x = composition.width / 2, y = composition.height / 2] = mask.points[0] ?? [];
    const [rx = composition.width / 4, ry = composition.height / 4] = mask.points[1] ?? [];
    context.ellipse(x - composition.width / 2, y - composition.height / 2, Math.abs(rx), Math.abs(ry), 0, 0, Math.PI * 2);
  } else if (mask.type === 'path' && mask.points.length > 2) {
    mask.points.forEach(([x, y], index) => index ? context.lineTo(x - composition.width / 2, y - composition.height / 2) : context.moveTo(x - composition.width / 2, y - composition.height / 2));
    context.closePath();
  } else {
    const [x = composition.width * 0.25, y = composition.height * 0.25] = mask.points[0] ?? [];
    const [w = composition.width * 0.5, h = composition.height * 0.5] = mask.points[1] ?? [];
    context.rect(x - composition.width / 2, y - composition.height / 2, w, h);
  }
  context.clip();
}

async function drawMedia(context: CanvasRenderingContext2D, project: Project, composition: Composition, layer: Layer, time: number, playing: boolean) {
  if (!layer.assetId) return;
  const asset = project.assets[layer.assetId];
  if (!asset || asset.missing) return;
  const element = await mediaRegistry.element(project.id, asset);
  if (!element) return;
  if (element instanceof HTMLVideoElement) {
    const sourceTime = Math.min(Math.max(0, sourceTimeAt(layer, time)), Math.max(0, element.duration - 0.01));
    if (playing) {
      element.playbackRate = layer.speed;
      if (Math.abs(element.currentTime - sourceTime) > 0.3) element.currentTime = sourceTime;
      if (element.paused) void element.play().catch(() => undefined);
    } else {
      element.pause();
      if (Math.abs(element.currentTime - sourceTime) > 1 / composition.fps) element.currentTime = sourceTime;
    }
    const width = asset.width ?? element.videoWidth ?? composition.width;
    const height = asset.height ?? element.videoHeight ?? composition.height;
    if (element.readyState >= 2) context.drawImage(element, -width / 2, -height / 2, width, height);
  } else {
    const width = asset.width ?? element.naturalWidth ?? composition.width;
    const height = asset.height ?? element.naturalHeight ?? composition.height;
    if (element.complete) context.drawImage(element, -width / 2, -height / 2, width, height);
  }
}

function drawText(context: CanvasRenderingContext2D, layer: Layer) {
  context.fillStyle = layer.color ?? '#ffffff';
  context.font = `${layer.fontWeight ?? 600} ${layer.fontSize ?? 64}px ${layer.fontFamily ?? 'Inter, sans-serif'}`;
  context.textAlign = layer.textAlign ?? 'center';
  context.textBaseline = 'middle';
  const lines = (layer.text ?? 'Text').split('\n');
  const lineHeight = (layer.fontSize ?? 64) * 1.18;
  if (layer.type === 'caption') {
    const widths = lines.map((line) => context.measureText(line).width);
    const maxWidth = Math.max(...widths, 100);
    context.fillStyle = 'rgba(0,0,0,.68)';
    context.roundRect(-maxWidth / 2 - 24, -lineHeight * lines.length / 2 - 10, maxWidth + 48, lineHeight * lines.length + 20, 12);
    context.fill();
    context.fillStyle = layer.color ?? '#ffffff';
    context.strokeStyle = 'rgba(0,0,0,.9)';
    context.lineWidth = Math.max(2, (layer.fontSize ?? 46) * 0.06);
  }
  lines.forEach((line, index) => {
    const y = (index - (lines.length - 1) / 2) * lineHeight;
    if (layer.type === 'caption') context.strokeText(line, 0, y);
    context.fillText(line, 0, y);
  });
}

function drawShape(context: CanvasRenderingContext2D, composition: Composition, layer: Layer) {
  context.fillStyle = layer.color ?? '#e6ff55';
  context.fillRect(-composition.width / 2, -composition.height / 2, composition.width, composition.height);
}

function applyPostEffects(context: CanvasRenderingContext2D, canvas: HTMLCanvasElement, layer: Layer) {
  const vignette = Number(layer.effects.find((effect) => effect.enabled && effect.type === 'vignette')?.params.value ?? 0);
  if (vignette > 0) {
    const gradient = context.createRadialGradient(canvas.width / 2, canvas.height / 2, canvas.height * 0.1, canvas.width / 2, canvas.height / 2, canvas.width * 0.72);
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(1, `rgba(0,0,0,${Math.min(0.9, vignette / 100)})`);
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
}

export async function renderComposition(canvas: HTMLCanvasElement, project: Project, time: number, playing = false, quality = 1): Promise<void> {
  const composition = project.compositions[project.activeCompositionId];
  const width = Math.max(1, Math.round(composition.width * quality));
  const height = Math.max(1, Math.round(composition.height * quality));
  if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
  const context = canvas.getContext('2d', { alpha: true });
  if (!context) return;
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.save();
  context.scale(quality, quality);
  context.fillStyle = composition.backgroundColor;
  context.fillRect(0, 0, composition.width, composition.height);
  const layers = activeLayersAt(project, composition, time).reverse();
  for (const layer of layers) {
    if (layer.type === 'audio' || layer.type === 'null' || (layer.type === 'group' && !layer.nestedCompositionId)) continue;
    if (layer.type === 'adjustment') {
      const copy = document.createElement('canvas');
      copy.width = canvas.width;
      copy.height = canvas.height;
      copy.getContext('2d')?.drawImage(canvas, 0, 0);
      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.filter = effectToCanvasFilter(layer.effects);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(copy, 0, 0);
      context.restore();
      applyPostEffects(context, canvas, layer);
      continue;
    }
    const transform = resolvedTransform(layer, time);
    context.save();
    context.globalCompositeOperation = blendModes[layer.blendMode];
    context.globalAlpha = Math.max(0, Math.min(1, transform.opacity));
    context.filter = effectToCanvasFilter(layer.effects);
    context.translate(composition.width / 2 + transform.position[0], composition.height / 2 + transform.position[1]);
    context.rotate(transform.rotation[2] * Math.PI / 180);
    context.scale(transform.scale[0], transform.scale[1]);
    applyMask(context, layer, composition);
    if (layer.type === 'video' || layer.type === 'image') await drawMedia(context, project, composition, layer, time, playing);
    else if (layer.type === 'text' || layer.type === 'caption') drawText(context, layer);
    else if (layer.type === 'shape') drawShape(context, composition, layer);
    else if (layer.type === 'group' && layer.nestedCompositionId && project.compositions[layer.nestedCompositionId]) {
      let nestedCanvas = nestedCanvases.get(layer.nestedCompositionId);
      if (!nestedCanvas) { nestedCanvas = document.createElement('canvas'); nestedCanvases.set(layer.nestedCompositionId, nestedCanvas); }
      await renderComposition(nestedCanvas, { ...project, activeCompositionId: layer.nestedCompositionId }, Math.max(0, time - layer.start), playing, quality);
      context.drawImage(nestedCanvas, -composition.width / 2, -composition.height / 2, composition.width, composition.height);
    }
    else if (layer.type === 'scene3d') {
      const { threeSceneRenderer } = await import('./threeRenderer');
      const sceneCanvas = threeSceneRenderer.render(layer, Math.min(composition.width, 960), Math.min(composition.height, 540), time - layer.start);
      if (sceneCanvas) context.drawImage(sceneCanvas, -composition.width / 2, -composition.height / 2, composition.width, composition.height);
    }
    context.restore();
  }
  context.restore();
}
